if (!customElements.get('shoppable-image')) {
  class ShoppableImage extends HTMLElement {
    // Index of the product shown by default on desktop (updated on click).
    #committedIndex = 0;
    // Index of the product temporarily previewed on hover, or null when no
    // hotspot is hovered (then the committed product is shown).
    #previewIndex = null;

    connectedCallback() {
      this._abortController = new AbortController();
      const { signal } = this._abortController;

      // Blank-product blocks are skipped on the storefront, so the first
      // rendered card is not necessarily data-index="0". The section tells us
      // which one it rendered visible; without this the panel would start with
      // every card hidden whenever block 0 has no product.
      const initial = parseInt(this.dataset.initialIndex ?? '0', 10);
      if (!Number.isNaN(initial)) this.#committedIndex = initial;

      this.addEventListener('click', this._onClick.bind(this), { signal });

      // Desktop hover preview: hovering a hotspot previews its product; the
      // committed product returns once the pointer leaves the image. Gated on a
      // real hover-capable pointer at desktop width so it never fires on touch
      // (where the single mobile dot fold-out is the interaction instead).
      if (window.matchMedia('(hover: hover) and (min-width: 1024px)').matches) {
        this.addEventListener('mouseover', this._onHover.bind(this), { signal });
        const image = this.querySelector('.shoppable-image__image');
        image?.addEventListener('mouseleave', this._onLeave.bind(this), { signal });
      }

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (evt) => {
          const index = parseInt(evt.target.dataset.index ?? '0', 10);
          this.#committedIndex = index;
          this.#previewIndex = null;
          this._render();
        }, { signal });
      }

      this._render();
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    _onClick(evt) {
      // Mobile fold-out toggle takes priority over hotspot clicks.
      const toggle = evt.target.closest('.shoppable-image__mobile-toggle');
      if (toggle) {
        this._toggleMobileCards(toggle);
        return;
      }

      const btn = evt.target.closest('.hotspot__btn');
      if (!btn) return;
      // Clicking a hotspot commits its product as the new default.
      this.#committedIndex = parseInt(btn.closest('.hotspot')?.dataset.index ?? '0', 10);
      this.#previewIndex = null;
      this._render();
    }

    _onHover(evt) {
      const hotspot = evt.target.closest('.hotspot');
      if (!hotspot) return;
      const index = parseInt(hotspot.dataset.index ?? '0', 10);
      if (index === this.#previewIndex) return;
      this.#previewIndex = index;
      this._render();
    }

    _onLeave() {
      if (this.#previewIndex === null) return;
      this.#previewIndex = null;
      this._render();
    }

    _toggleMobileCards(toggle) {
      const cards = this.querySelector('.shoppable-image__mobile-cards');
      if (!cards) return;
      const open = !cards.classList.contains('is-open');
      cards.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
    }

    _render() {
      // Hover preview wins over the committed selection; fall back to committed.
      const shown = this.#previewIndex ?? this.#committedIndex;

      // Active styling (and aria-pressed) tracks the committed selection so it
      // doesn't flicker on hover — hover gets its own :hover style in CSS. The
      // previewed card is the one made visible in the panel.
      this.querySelectorAll('.hotspot').forEach((el) => {
        const i = parseInt(el.dataset.index, 10);
        const committed = i === this.#committedIndex;
        el.classList.toggle('is-active', committed);
        el.querySelector('.hotspot__btn')?.setAttribute('aria-pressed', String(committed));
      });

      this.querySelectorAll('.shoppable-product-card').forEach((el) => {
        el.classList.toggle('is-hidden', parseInt(el.dataset.index, 10) !== shown);
      });
    }
  }

  customElements.define('shoppable-image', ShoppableImage);
}
